import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import type { Asset } from '../types'

/**
 * One image in the grid, and nothing else.
 *
 * There used to be a caption under every card — the name, the frame it came
 * from, its pixel size. It is gone. Nearly every image arrives from Miro called
 * "Untitled image", the frame is already what the filter chips say, and a
 * library is looked at rather than read. What the name was for is still served:
 * screen readers get it from the card's label, and a picture that fails to load
 * shows it in place of the image.
 */
interface AssetCardProps {
  asset: Asset
  selected: boolean
  onToggle: (asset: Asset) => void
  /** Shortcut button in the bottom-right corner, e.g. "Add" in the Miro panel */
  actionLabel?: string
  onAction?: (asset: Asset) => void
  /** Sweep-to-select. See usePaintSelection. */
  onPointerDown?: (asset: Asset, selected: boolean, event: ReactPointerEvent) => void
  onPointerEnter?: (asset: Asset, selected: boolean) => void
  /** True when the click that follows belongs to a sweep and should be ignored. */
  shouldSwallowClick?: () => boolean
  /** The exact box the row worked out for this image. */
  width?: number
  height?: number
  /**
   * Fill an equal cell instead of being sized to its own proportions. Used
   * where a row is too narrow to justify — see AssetGrid.
   */
  cell?: boolean
  /**
   * An extra class on the card -- `miro-draggable` in the Miro panel, which is
   * how Miro's SDK knows a card can be dragged onto the board. The card also
   * carries its asset id, for whoever handles the drop.
   */
  dragClass?: string
  /**
   * Held still on the card for LONG_PRESS_MS: an administrator's way into the
   * picture's tags in a library (Huanrui, 2026-10-12). The click that ends the
   * press does not select it.
   */
  onLongPress?: (asset: Asset) => void
  /**
   * The View larger button in the top-right corner, shown on hover (Huanrui,
   * 2026-10-15): opens the picture over the page without selecting it.
   */
  onView?: (asset: Asset) => void
  /** View details, a pill at the foot of the card (Amazon's products, 2026-10-21). */
  onDetails?: (asset: Asset) => void
}

/** How long the press has to be. Three seconds, as asked. */
const LONG_PRESS_MS = 3000
/** A press that wanders further than this is a sweep or a scroll, not a hold. */
const LONG_PRESS_SLOP_PX = 8

/** Used until an image of unknown size has loaded and measured itself. */
const FALLBACK_RATIO = 4 / 5
/**
 * Every card is the same height, so its proportions decide its width. A
 * panorama or a hairline crop would otherwise be drawn as a stripe nobody can
 * click; clamping keeps every card a usable shape, and the picture inside is
 * fitted rather than cropped so nothing is lost.
 */
const MIN_RATIO = 0.4
const MAX_RATIO = 3
/** Waits before trying the thumbnail again, in milliseconds. */
const THUMB_RETRY_WAITS_MS = [800, 2500]
/** Wait before trying the full-size file a second time. */
const ORIGINAL_RETRY_WAIT_MS = 6000

/**
 * What the browser knows about a request that failed: its HTTP status and how
 * long it took. `onError` says nothing, so this is the only way to tell
 * "Storage said 500" from "Storage never answered". Signed links are cut at
 * the `?` so the token never reaches the console.
 */
function describeFailure(url: string): string {
  const where = url.split('?')[0].split('/object/sign/')[1] ?? url.split('?')[0]
  try {
    const entries = performance.getEntriesByName(url) as (PerformanceResourceTiming & { responseStatus?: number })[]
    const last = entries[entries.length - 1]
    if (!last) return `${where} (no timing)`
    const status = last.responseStatus ? `HTTP ${last.responseStatus}` : 'no answer'
    return `${where} (${status}, ${Math.round(last.duration)} ms)`
  } catch {
    return where
  }
}
/** How many times a card that gave up tries again on scrolling back into view. */
const MAX_REVIVALS = 3

export function AssetCard({
  asset,
  selected,
  onToggle,
  actionLabel,
  onAction,
  onPointerDown,
  onPointerEnter,
  shouldSwallowClick,
  width,
  height,
  cell = false,
  dragClass,
  onLongPress,
  onView,
  onDetails,
}: AssetCardProps) {
  const known = asset.width > 0 && asset.height > 0
  const [loaded, setLoaded] = useState(false)

  /**
   * What to try, in order, before giving up on showing this image.
   *
   * A refused request is usually a passing thing — Storage busy for a moment,
   * a dropped connection — rather than proof that the picture does not exist.
   * So the thumbnail is tried again, a little apart, BEFORE the full-size file.
   *
   * The order is the point (2026-10-01). It used to be thumbnail, then the
   * original at once. The original is the full photograph, often megabytes, so
   * one hiccup on a 30 kB thumbnail left the card grey for as long as that
   * download took — while the same thumbnail showed instantly in the selection
   * tray. The original is now the last resort, for a thumbnail that is really
   * missing. `wait` is how long to hold off before that try.
   */
  const tries = useMemo(() => {
    const thumb = asset.thumbnailPath
    const original = asset.originalPath && asset.originalPath !== thumb ? asset.originalPath : ''
    const again = (url: string, n: number) => url + (url.includes('?') ? '&' : '?') + `retry=${n}`
    const list: { url: string; wait: number }[] = []
    if (thumb) {
      list.push({ url: thumb, wait: 0 })
      THUMB_RETRY_WAITS_MS.forEach((wait, i) => list.push({ url: again(thumb, i + 1), wait }))
    }
    if (original) {
      list.push({ url: original, wait: 0 })
      const waits = thumb ? [ORIGINAL_RETRY_WAIT_MS] : [...THUMB_RETRY_WAITS_MS, ORIGINAL_RETRY_WAIT_MS]
      waits.forEach((wait, i) => list.push({ url: again(original, i + 1), wait }))
    }
    return list
  }, [asset.thumbnailPath, asset.originalPath])

  const [step, setStep] = useState(0)
  /** Between a failed try and the next one, nothing is drawn. */
  const [waiting, setWaiting] = useState(false)
  const failed = tries.length === 0 || step >= tries.length
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const revivals = useRef(0)

  // New links for this card (a fresh page, a re-sign) start from the top.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    setStep(0)
    setWaiting(false)
    revivals.current = 0
  }, [tries])

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  // Given up, then scrolled away and back: one more round, a few times at most.
  // Whatever was wrong has usually passed by then, and it costs nothing for
  // cards nobody looks at again.
  useEffect(() => {
    const node = cardRef.current
    if (!failed || tries.length === 0 || !node || typeof IntersectionObserver === 'undefined') return
    if (revivals.current >= MAX_REVIVALS) return
    let wasOut = false
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((entry) => entry.isIntersecting)
      if (!visible) wasOut = true
      else if (wasOut) {
        revivals.current += 1
        setStep(0)
        observer.disconnect()
      }
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [failed, tries])

  // Images dropped into library/ have no stored dimensions, so the card
  // measures them the moment the browser has the file.
  const [measured, setMeasured] = useState<{ width: number; height: number } | null>(null)

  // The long press. `fired` outlives the press so the click that ends it is
  // swallowed; the next press clears it, in case that click landed elsewhere
  // (the dialog opens over the card, so it usually does).
  const press = useRef<{ timer: ReturnType<typeof setTimeout> | null; x: number; y: number; fired: boolean }>({
    timer: null,
    x: 0,
    y: 0,
    fired: false,
  })
  const [holding, setHolding] = useState(false)
  const endPress = () => {
    if (press.current.timer) clearTimeout(press.current.timer)
    press.current.timer = null
    setHolding(false)
  }
  useEffect(
    () => () => {
      if (press.current.timer) clearTimeout(press.current.timer)
    },
    [],
  )
  const startPress = (event: ReactPointerEvent) => {
    press.current.fired = false
    if (!onLongPress || (event.pointerType === 'mouse' && event.button !== 0)) return
    endPress()
    press.current.x = event.clientX
    press.current.y = event.clientY
    setHolding(true)
    press.current.timer = setTimeout(() => {
      press.current.timer = null
      press.current.fired = true
      setHolding(false)
      onLongPress(asset)
    }, LONG_PRESS_MS)
  }

  const size = known ? { width: asset.width, height: asset.height } : measured
  const ratio = size ? size.width / size.height : FALLBACK_RATIO
  const aspect = String(Math.min(MAX_RATIO, Math.max(MIN_RATIO, ratio)))

  return (
    // The whole card toggles, title and all — not just the picture. The action
    // button inside stops its own click from reaching this.
    <div
      ref={cardRef}
      className={`ww-card${selected ? ' ww-card--selected' : ''}${cell ? ' ww-card--cell' : ''}${dragClass ? ` ${dragClass}` : ''}`}
      data-asset-id={dragClass ? asset.id : undefined}
      // The row has already worked out the exact box, so the browser needs no
      // help guessing — which is also what lets it skip a card that is off
      // screen without the layout shifting when it comes back.
      style={
        cell
          ? ({ '--ww-card-ratio': aspect } as CSSProperties)
          : ({
              '--ww-card-ratio': aspect,
              width: width ? `${width}px` : undefined,
              height: height ? `${height}px` : undefined,
              containIntrinsicSize: width && height ? `${width}px ${height}px` : undefined,
            } as CSSProperties)
      }
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${selected ? 'Deselect' : 'Select'} ${asset.title}`}
      onPointerDown={(event) => {
        startPress(event)
        onPointerDown?.(asset, selected, event)
      }}
      onPointerMove={
        onLongPress
          ? (event) => {
              if (!press.current.timer) return
              const dx = event.clientX - press.current.x
              const dy = event.clientY - press.current.y
              if (dx * dx + dy * dy > LONG_PRESS_SLOP_PX * LONG_PRESS_SLOP_PX) endPress()
            }
          : undefined
      }
      onPointerUp={onLongPress ? endPress : undefined}
      onPointerLeave={onLongPress ? endPress : undefined}
      onPointerCancel={onLongPress ? endPress : undefined}
      onContextMenu={onLongPress ? (event) => holding && event.preventDefault() : undefined}
      onPointerEnter={() => onPointerEnter?.(asset, selected)}
      onClick={() => {
        // A sweep ends with a click on whichever card it finished over, and
        // that card has already been dealt with.
        const swept = shouldSwallowClick?.()
        if (press.current.fired) {
          press.current.fired = false
          return
        }
        if (swept) return
        onToggle(asset)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onToggle(asset)
        }
      }}
    >
      <div
        className="ww-card__media"
        style={
          cell
            ? undefined
            : height
              ? // The card already has the exact box, so the picture area fills
                // it. Leaving the width to work itself out was the bug that
                // burst the grid: with `width: auto` and an image inside asking
                // for 100%, the browser falls back to the file's own pixel
                // width — a 2000px photograph made a 2000px card.
                { width: '100%', height: '100%' }
              : { aspectRatio: aspect }
        }
      >
        {waiting ? null : !failed ? (
          <img
            className={`ww-card__img${loaded ? ' ww-card__img--loaded' : ''}`}
            src={tries[step].url}
            alt={asset.title}
            loading="lazy"
            decoding="async"
            // Firefox ignores the CSS for this, and a picture that tears loose
            // mid-sweep leaves a ghost image following the cursor.
            draggable={false}
            onLoad={(event) => {
              const image = event.currentTarget
              if (!known && image.naturalWidth > 0) {
                setMeasured({ width: image.naturalWidth, height: image.naturalHeight })
              }
              setLoaded(true)
            }}
            onError={() => {
              const next = step + 1
              setLoaded(false)
              // Every miss is noted, not only the last: the pattern of statuses
              // is what says whether Storage is refusing or just slow.
              console.warn(`[WonderWorks] image try ${step + 1}/${tries.length} failed: ${describeFailure(tries[step].url)}`)
              if (next >= tries.length) {
                console.warn(`[WonderWorks] could not load ${asset.title}`)
                setStep(next)
                return
              }
              const wait = tries[next].wait
              if (wait <= 0) {
                setStep(next)
                return
              }
              setWaiting(true)
              if (timer.current) clearTimeout(timer.current)
              timer.current = setTimeout(() => {
                timer.current = null
                setWaiting(false)
                setStep(next)
              }, wait)
            }}
          />
        ) : (
          <span className="ww-card__fallback">{asset.title}</span>
        )}
        <span className="ww-card__check" aria-hidden="true">
          ✓
        </span>
        {onDetails ? (
          <button
            type="button"
            className="ww-card__details"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation()
              onDetails(asset)
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            View details
          </button>
        ) : null}
        {onView ? (
          <button
            type="button"
            className="ww-card__view"
            title="View larger"
            aria-label={`View ${asset.title} larger`}
            // Not the start of a sweep or a long press, and not a selection.
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation()
              onView(asset)
            }}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <svg width="13" height="13" viewBox="0 0 14 14" aria-hidden="true">
              <path
                d="M8.5 1.5h4v4M12.5 1.5L8 6M5.5 12.5h-4v-4M1.5 12.5L6 8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        ) : null}
        {holding ? (
          // Invisible for an ordinary click: it fades in after a moment, then
          // fills over the rest of the three seconds (components.css).
          <span className="ww-card__hold" aria-hidden="true">
            <svg viewBox="0 0 36 36">
              <circle className="ww-card__hold-track" cx="18" cy="18" r="15" />
              <circle
                className="ww-card__hold-fill"
                cx="18"
                cy="18"
                r="15"
                style={{ animationDuration: `${LONG_PRESS_MS}ms` }}
              />
            </svg>
          </span>
        ) : null}
      </div>

      {actionLabel && onAction ? (
        <button
          type="button"
          className="ww-card__action"
          onClick={(event) => {
            event.stopPropagation()
            onAction(asset)
          }}
        >
          {actionLabel}
        </button>
      ) : null}

    </div>
  )
}
